import React, { useState, useCallback } from 'react';
import type { POI } from '../types';
import { askGemini } from '../services/geminiService';
import { GUIDES } from '../data/guides';
import { TYPE_LABELS } from '../constants';
import { t, LOCALE } from '../lib/i18n';

interface GeminiAssistantProps {
  poi: POI;
}

export const GeminiAssistant: React.FC<GeminiAssistantProps> = ({ poi }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAsk = useCallback(async () => {
    if (!question.trim()) return;

    setIsLoading(true);
    setAnswer('');
    setError('');

    const guideNames = poi.sources
      .map(s => GUIDES[s.guideId]?.name)
      .filter(Boolean)
      .join(', ');

    const context = [
      `Name: ${poi.name}`,
      `Type: ${TYPE_LABELS[poi.type]}`,
      poi.description ? `Description: ${poi.description}` : null,
      poi.address ? `Address: ${poi.address}` : null,
      poi.city ? `City: ${poi.city}` : null,
      guideNames ? `Featured in: ${guideNames}` : null,
    ].filter(Boolean).join('\n');
    
    try {
        const result = await askGemini(context, question, LOCALE);
        setAnswer(result);
    } catch (err) {
        setError(t('ai.error'));
        console.error(err);
    } finally {
        setIsLoading(false);
        setQuestion('');
    }
  }, [question, poi]);

  const handleSuggestionClick = (suggestion: string) => {
    setQuestion(suggestion);
  };

  const suggestions = [
    t('ai.suggestion.signature'),
    t('ai.suggestion.ambiance'),
    t('ai.suggestion.occasion'),
  ];

  return (
    <div className="mt-4 text-gray-800">
      <h4 className="font-bold text-md text-cyan-600">{t('ai.title')}</h4>
      <p className="text-xs text-gray-500 mb-2">{t('ai.subtitle', { name: poi.name })}</p>

      <div className="flex flex-wrap gap-1 mb-3">
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => handleSuggestionClick(s)}
            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-full transition-colors"
          >
            {s}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t('ai.placeholder')}
          className="flex-grow bg-white border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 transition placeholder-gray-400 text-gray-900"
          onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
          disabled={isLoading}
        />
        <button
          onClick={handleAsk}
          disabled={isLoading || !question.trim()}
          className="bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-md transition-colors text-sm"
        >
          {isLoading ? t('ai.askingLabel') : t('ai.askButton')}
        </button>
      </div>

      {isLoading && <p className="mt-4 text-sm text-center text-gray-500">{t('ai.thinking')}</p>}
      {answer && (
        <div className="mt-4 p-3 bg-gray-100 rounded-lg">
          <p className="text-sm whitespace-pre-wrap font-sans text-gray-800">{answer}</p>
        </div>
      )}
      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
    </div>
  );
};
